import React, { useRef, useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import * as yup from 'yup';
import { yupResolver } from '@hookform/resolvers/yup';
import {
  Avatar,
  Box,
  Button,
  Divider,
  Flex,
  FormControl,
  FormLabel,
  IconButton,
  Input,
  Select,
  Switch,
  Text,
  VStack,
  HStack,
  useRadio,
  useRadioGroup,
  UseRadioProps,
  Icon,
  Spinner,
} from '@chakra-ui/react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { useNavigate } from 'react-router-dom';
import { Camera, Key, Trash, Medal, Diamond, WhatsappLogo } from 'phosphor-react';
import { InputForm } from '../../inputs/forms/InputForm';
import { IUser, useUsers, NivelUsuario } from '../../../hooks/useUsers';
import { IGroup, useGroups } from '../../../hooks/useGroups';
import { IDepartamento, useDepartamentos } from '../../../hooks/useDepartamentos';
import { IData, IDataNoPagination, IPagination } from '../../../types/shared';
import SelectInputForm from '../../inputs/forms/SelectInputForm';
import MultiSelectInputForm from '../../inputs/forms/MultiSelectInputForm';
import { useAuth } from '../../../providers/auth';
import waApi from '../../../services/whatsapp/waApi';

// Configuração dos níveis
const NIVEIS_CONFIG: {
  value: NivelUsuario;
  label: string;
  color: string;
  bgColor: string;
  borderColor: string;
  icon: typeof Medal;
}[] = [
  {
    value: 'Latão',
    label: 'Latão',
    color: '#B87333',
    bgColor: 'orange.50',
    borderColor: 'orange.200',
    icon: Medal,
  },
  {
    value: 'Prata',
    label: 'Prata',
    color: '#A0AEC0',
    bgColor: 'gray.50',
    borderColor: 'gray.300',
    icon: Medal,
  },
  {
    value: 'Ouro',
    label: 'Ouro',
    color: '#D69E2E',
    bgColor: 'yellow.50',
    borderColor: 'yellow.400',
    icon: Medal,
  },
  {
    value: 'Diamante',
    label: 'Diamante',
    color: '#38B2AC',
    bgColor: 'cyan.50',
    borderColor: 'cyan.400',
    icon: Diamond,
  },
];

// Componente de Radio Card para Nível
interface NivelRadioCardProps extends UseRadioProps {
  children: React.ReactNode;
  nivelConfig: (typeof NIVEIS_CONFIG)[0];
}

function NivelRadioCard({ nivelConfig, children, ...props }: NivelRadioCardProps) {
  const { getInputProps, getRadioProps } = useRadio(props);
  const input = getInputProps();
  const checkbox = getRadioProps();

  return (
    <Box as="label" flex={1} cursor="pointer">
      <input {...input} />
      <Box
        {...checkbox}
        borderWidth="2px"
        borderRadius="xl"
        borderColor={nivelConfig.borderColor}
        bg={nivelConfig.bgColor}
        px={4}
        py={3}
        textAlign="center"
        transition="all 0.2s"
        _checked={{
          borderColor: nivelConfig.color,
          bg: nivelConfig.bgColor,
          boxShadow: `0 0 0 2px ${nivelConfig.color}`,
          transform: 'scale(1.02)',
        }}
        _hover={{
          borderColor: nivelConfig.color,
          transform: 'scale(1.02)',
        }}
      >
        <VStack spacing={1}>
          <Icon as={nivelConfig.icon} boxSize={6} color={nivelConfig.color} weight="fill" />
          <Text fontSize="sm" fontWeight="bold" color={nivelConfig.color}>
            {children}
          </Text>
        </VStack>
      </Box>
    </Box>
  );
}

const getSchema = (isNewUser: boolean) =>
  yup.object().shape({
    id: yup.number(),
    nome: yup.string().required('Este campo é obrigatório!'),
    email: yup.string().email('E-mail inválido').required('Este campo é obrigatório!'),
    grupo: yup.mixed<IGroup>().nullable().required('Este campo é obrigatório!'),
    grupo_id: yup.number(),
    departamentos: yup.array().optional(),
    ativo: yup.boolean(),
    is_sympla: yup.boolean(),
    nivel: yup.string().oneOf(['Latão', 'Prata', 'Ouro', 'Diamante']).optional(),
    password: isNewUser
      ? yup
          .string()
          .required('A senha é obrigatória para novos usuários!')
          .min(6, 'A senha deve ter pelo menos 6 caracteres')
      : yup.string().optional(),
    confirmPassword: yup.string().when('password', {
      is: (value: string | undefined) => !!value,
      then: schema2 =>
        schema2
          .required('Confirme a senha')
          .oneOf([yup.ref('password')], 'As senhas não são iguais'),
      otherwise: schema2 => schema2.optional(),
    }),
    data_admissao: yup.string().optional(),
    genero: yup.string().optional(),
  });

interface IForm {
  id?: number;
  nome: string;
  email: string;
  grupo: IGroup;
  grupo_id?: number;
  departamentos?: { value: string; label: string }[];
  ativo?: boolean;
  is_sympla?: boolean;
  nivel?: NivelUsuario;
  password?: string;
  confirmPassword?: string;
  imagem_url?: string;
  data_admissao?: string;
  genero?: string;
}

interface UsersFormProps {
  defaultValues?: IForm;
}

const generatePassword = () => {
  const chars = 'abcdefghijklmnopqrstuvwxyzABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789!@#$%&*';
  let password = '';
  for (let i = 0; i < 16; i += 1) {
    password += chars.charAt(Math.floor(Math.random() * chars.length));
  }
  return password;
};

export default function UsersForm({ defaultValues }: UsersFormProps) {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { createUser, updateUser, uploadImage, deleteImage } = useUsers();
  const { getGroups } = useGroups();
  const { getDepartamentos } = useDepartamentos();
  const { getUser, user } = useAuth();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string | undefined>(defaultValues?.imagem_url);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isRemovingImage, setIsRemovingImage] = useState(false);

  // WhatsApp numbers state
  const [allWaNumbers, setAllWaNumbers] = useState<{ id: string; label: string }[]>([]);
  const [selectedWaNumberIds, setSelectedWaNumberIds] = useState<string[]>([]);
  const [waNumbersLoading, setWaNumbersLoading] = useState(false);

  const isNewUser = !defaultValues;

  // Verifica se o usuário atual é administrador (aceita número ou string)
  const GRUPO_ADMIN_ID = 1;
  const GRUPO_CLIENTE_ID = 7;
  const isAdmin =
    user && (Number(user.grupo_id) === GRUPO_ADMIN_ID || user.grupo_id === GRUPO_ADMIN_ID);

  const { mutateAsync } = useMutation({
    mutationFn: defaultValues ? updateUser : createUser,
  });

  const queryKey = ['GroupsSelect', { page: 1, size: 30, search: '' }];

  const { data, isFetching } = useQuery<IData<IGroup[]>>({
    queryKey,
    queryFn: getGroups,
  });

  const { items: allGroups } = data?.data ?? { items: [], ...({} as IPagination) };
  const groups = allGroups.filter(g => g.id !== GRUPO_CLIENTE_ID);

  // Query para departamentos
  const { data: departamentosData, isFetching: isFetchingDepartamentos } = useQuery<
    IDataNoPagination<IDepartamento[]>
  >({
    queryKey: ['DepartamentosSelect'],
    queryFn: getDepartamentos,
  });

  const departamentos = departamentosData?.data ?? [];

  // Carregar números WhatsApp e permissões do usuário
  React.useEffect(() => {
    if (!isAdmin) return;

    const loadWaNumbers = async () => {
      setWaNumbersLoading(true);
      try {
        const numbersResp = await waApi.get('/wa-numbers');
        setAllWaNumbers(
          (numbersResp.data || []).map((n: any) => ({
            id: n.id,
            label: `${n.label}${n.phone_number ? ` (${n.phone_number})` : ''}`,
          })),
        );

        if (defaultValues?.id) {
          const permsResp = await waApi.get(`/wa-numbers/user-permissions/${defaultValues.id}`);
          setSelectedWaNumberIds(permsResp.data?.wa_number_ids || []);
        }
      } catch {
        // API indisponível — ignora silenciosamente
      } finally {
        setWaNumbersLoading(false);
      }
    };

    loadWaNumbers();
  }, [defaultValues?.id, isAdmin]);

  const handleToggleWaNumber = (numberId: string) => {
    setSelectedWaNumberIds(prev =>
      prev.includes(numberId) ? prev.filter(id => id !== numberId) : [...prev, numberId],
    );
  };

  const formMethods = useForm<IForm>({
    resolver: yupResolver(getSchema(isNewUser)),
    defaultValues: {
      id: defaultValues?.id,
      nome: defaultValues?.nome ?? '',
      email: defaultValues?.email ?? '',
      grupo: defaultValues?.grupo ?? undefined,
      grupo_id: defaultValues?.grupo_id,
      departamentos:
        defaultValues?.departamentos?.map((d: any) => ({
          value: String(d.id),
          label: d.nome,
        })) ?? [],
      ativo: defaultValues?.ativo ?? true,
      is_sympla: defaultValues?.is_sympla ?? false,
      nivel: defaultValues?.nivel ?? undefined,
      password: undefined,
      confirmPassword: undefined,
      data_admissao: defaultValues?.data_admissao ?? '',
      genero: defaultValues?.genero ?? '',
    },
  });

  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      const allowedTypes = ['image/jpeg', 'image/png', 'image/gif', 'image/webp'];
      if (!allowedTypes.includes(file.type)) {
        toast.error('Tipo de arquivo não permitido. Use: JPEG, PNG, GIF ou WebP.', {
          position: 'top-right',
        });
        return;
      }
      if (file.size > 5 * 1024 * 1024) {
        toast.error('Arquivo muito grande. Tamanho máximo: 5MB.', { position: 'top-right' });
        return;
      }
      setSelectedFile(file);
      setPreviewUrl(URL.createObjectURL(file));
    }
  };

  const handleGeneratePassword = () => {
    const password = generatePassword();
    formMethods.setValue('password', password);
    formMethods.setValue('confirmPassword', password);
    navigator.clipboard.writeText(password);
    toast.success('Senha gerada e copiada para a área de transferência!', {
      position: 'top-right',
    });
  };

  const handleRemoveImage = async () => {
    if (!defaultValues?.id) {
      setSelectedFile(null);
      setPreviewUrl(undefined);
      return;
    }

    setIsRemovingImage(true);
    try {
      await deleteImage(defaultValues.id);
      setSelectedFile(null);
      setPreviewUrl(undefined);
      queryClient.invalidateQueries({ queryKey: ['users'] });
      queryClient.invalidateQueries({ queryKey: ['getUser'] });
      await getUser();
      toast.success('Imagem removida com sucesso!', {
        position: 'top-right',
      });
    } catch (error: any) {
      let errorMessage = 'Erro ao remover imagem.';
      const detail = error?.response?.data?.detail;
      if (typeof detail === 'string') {
        errorMessage = detail;
      } else if (Array.isArray(detail) && detail.length > 0) {
        errorMessage = detail[0]?.msg || errorMessage;
      }
      toast.error(errorMessage, {
        position: 'top-right',
      });
    } finally {
      setIsRemovingImage(false);
    }
  };

  const onSubmit = async (values: IForm) => {
    setIsSubmitting(true);
    try {
      const departamentoIds = values.departamentos?.map(d => Number(d.value)) ?? [];
      const result = await mutateAsync({
        ...values,
        empresa_id: user?.empresa_id,
        grupo: undefined,
        grupo_id: values.grupo.id,
        departamentos: undefined,
        departamento_id: departamentoIds[0] ?? null,
        departamento_ids: departamentoIds,
        id: defaultValues?.id,
        ...(values.password && { password: values.password }),
        ...(values.confirmPassword && { confirmPassword: values.confirmPassword }),
      } as unknown as IUser);

      const userId = defaultValues?.id ?? (result as any)?.data?.id;
      if (selectedFile && userId) {
        try {
          await uploadImage(userId, selectedFile);
          queryClient.invalidateQueries({ queryKey: ['users'] });
        } catch (uploadError: any) {
          let uploadErrorMessage = 'Erro ao fazer upload da imagem.';

          // Verifica se é erro de CORS ou network
          if (!uploadError?.response) {
            uploadErrorMessage =
              'Erro de conexão com o servidor. Verifique se o backend está rodando e configurado para aceitar requisições CORS.';
          } else {
            const uploadDetail = uploadError.response.data?.detail;
            if (typeof uploadDetail === 'string') {
              uploadErrorMessage = uploadDetail;
            } else if (Array.isArray(uploadDetail) && uploadDetail.length > 0) {
              uploadErrorMessage = uploadDetail[0]?.msg || uploadErrorMessage;
            }
          }

          toast.error(uploadErrorMessage, {
            position: 'top-right',
          });
        }
      }

      // Salvar permissões WhatsApp
      if (isAdmin && userId) {
        try {
          await waApi.put(`/wa-numbers/user-permissions/${userId}`, {
            wa_number_ids: selectedWaNumberIds,
          });
        } catch {
          // falha silenciosa — não impede salvar o usuário
        }
      }

      await getUser();
      queryClient.invalidateQueries({ queryKey: ['users'] });
      toast.success(`Usuário ${defaultValues ? 'editado' : 'criado'} com sucesso!`, {
        position: 'top-right',
      });
      navigate(
        defaultValues?.id
          ? `/configuracoes/permissoes?usuario=${defaultValues.id}`
          : '/configuracoes/permissoes?modo=usuarios',
        { replace: true },
      );
    } catch (error: any) {
      let errorMessage = 'Erro interno no sistema, tente novamente mais tarde.';
      const detail = error?.response?.data?.detail;
      if (typeof detail === 'string') {
        errorMessage = detail;
      } else if (Array.isArray(detail) && detail[0]?.type === 'date_from_datetime_parsing') {
        errorMessage = 'A data de admissão deve ser preenchida';
      } else if (Array.isArray(detail) && detail.length > 0) {
        errorMessage = detail[0]?.msg || errorMessage;
      }
      toast.error(errorMessage, { position: 'top-right' });
    } finally {
      setIsSubmitting(false);
    }
  };

  const ativo = formMethods.watch('ativo');
  const isSympla = formMethods.watch('is_sympla');
  const nivel = formMethods.watch('nivel');

  const { getRootProps, getRadioProps } = useRadioGroup({
    name: 'nivel',
    value: nivel || '',
    onChange: (value: string) => {
      formMethods.setValue('nivel', value as NivelUsuario);
    },
  });

  const nivelGroup = getRootProps();

  return (
    <Box maxW="800px" mx="auto">
      <form onSubmit={formMethods.handleSubmit(onSubmit)}>
        <FormProvider {...formMethods}>
          <Box bg="white" borderRadius="xl" boxShadow="sm" overflow="hidden">
            {/* Header com Avatar */}
            <Box bg="gray.50" py={8} px={6}>
              <Flex direction="column" align="center" gap={3}>
                <Box position="relative">
                  <Avatar
                    size="2xl"
                    name={formMethods.watch('nome') || 'Usuário'}
                    src={previewUrl}
                    cursor="pointer"
                    onClick={() => fileInputRef.current?.click()}
                    bg="primary"
                    color="white"
                  />
                  <IconButton
                    aria-label="Alterar foto"
                    icon={<Camera size={18} weight="bold" />}
                    size="sm"
                    colorScheme="blue"
                    borderRadius="full"
                    position="absolute"
                    bottom={1}
                    right={1}
                    onClick={() => fileInputRef.current?.click()}
                  />
                  {(previewUrl || selectedFile) && (
                    <IconButton
                      aria-label="Remover foto"
                      icon={<Trash size={18} weight="bold" />}
                      size="sm"
                      colorScheme="red"
                      borderRadius="full"
                      position="absolute"
                      bottom={1}
                      left={1}
                      onClick={handleRemoveImage}
                      isLoading={isRemovingImage}
                    />
                  )}
                </Box>
                <Input
                  ref={fileInputRef}
                  type="file"
                  accept="image/jpeg,image/png,image/gif,image/webp"
                  onChange={handleFileSelect}
                  display="none"
                />
                <VStack spacing={0}>
                  <Text fontSize="lg" fontWeight="semibold" color="gray.700">
                    {formMethods.watch('nome') || 'Nome do Usuário'}
                  </Text>
                  <Text fontSize="sm" color="gray.500">
                    {formMethods.watch('email') || 'email@exemplo.com'}
                  </Text>
                </VStack>
                {selectedFile && (
                  <Text fontSize="xs" color="green.500" fontWeight="medium">
                    Nova imagem selecionada: {selectedFile.name}
                  </Text>
                )}
              </Flex>
            </Box>

            {/* Formulário */}
            <Box p={6}>
              <VStack spacing={6} align="stretch">
                {/* Dados Pessoais */}
                <Box>
                  <Text fontSize="sm" fontWeight="semibold" color="gray.600" mb={4}>
                    DADOS PESSOAIS
                  </Text>
                  <VStack spacing={4}>
                    <Flex gap={4} w="100%" direction={{ base: 'column', md: 'row' }}>
                      <Box flex={1}>
                        <InputForm name="nome" label="Nome completo" placeholder="Digite o nome" />
                      </Box>
                      <Box flex={1}>
                        <InputForm name="email" label="E-mail" placeholder="Digite o e-mail" />
                      </Box>
                    </Flex>
                    <Flex gap={4} w="100%" direction={{ base: 'column', md: 'row' }}>
                      <Box flex={1}>
                        <InputForm name="data_admissao" label="Data de Admissão" type="date" />
                      </Box>
                      <Box flex={1}>
                        <FormControl>
                          <FormLabel fontWeight="bold" mb={1}>
                            Gênero
                          </FormLabel>
                          <Select
                            value={formMethods.watch('genero') || ''}
                            onChange={e => formMethods.setValue('genero', e.target.value)}
                            placeholder="Selecione..."
                            height="44px"
                          >
                            <option value="Masculino">Masculino</option>
                            <option value="Feminino">Feminino</option>
                            <option value="Outro">Outro</option>
                            <option value="Prefiro não informar">Prefiro não informar</option>
                          </Select>
                        </FormControl>
                      </Box>
                    </Flex>
                  </VStack>
                </Box>

                <Divider />

                {/* Permissões */}
                <Box>
                  <Text fontSize="sm" fontWeight="semibold" color="gray.600" mb={4}>
                    PERMISSÕES E ACESSO
                  </Text>
                  <VStack spacing={4}>
                    <Flex gap={4} w="100%" direction={{ base: 'column', md: 'row' }}>
                      <Box flex={1}>
                        <SelectInputForm<IGroup>
                          name="grupo"
                          label="Grupo de permissões"
                          options={groups}
                          getOptionLabel={item => (item as IGroup).nome}
                          getOptionValue={item => String((item as IGroup).id)}
                          queryKeyName="GroupsSelect"
                          getOptions={getGroups}
                          isLoading={isFetching}
                        />
                      </Box>
                      <Box flex={1}>
                        <MultiSelectInputForm
                          name="departamentos"
                          label="Departamentos"
                          options={departamentos
                            .filter(d => d.status === 'ativo')
                            .map(d => ({ value: String(d.id), label: d.nome }))}
                          isLoading={isFetchingDepartamentos}
                          placeholder="Selecione departamentos"
                          maxSelectedOptions={10}
                        />
                      </Box>
                    </Flex>

                    <Flex
                      gap={4}
                      w="100%"
                      direction={{ base: 'column', md: 'row' }}
                      bg="gray.50"
                      p={4}
                      borderRadius="lg"
                    >
                      <FormControl display="flex" alignItems="center" flex={1}>
                        <Box flex={1}>
                          <FormLabel htmlFor="ativo" mb={0} fontSize="sm" fontWeight="medium">
                            Usuário ativo
                          </FormLabel>
                          <Text fontSize="xs" color="gray.500">
                            Permite acesso ao sistema
                          </Text>
                        </Box>
                        <Switch
                          id="ativo"
                          colorScheme="green"
                          size="lg"
                          isChecked={ativo}
                          onChange={e => formMethods.setValue('ativo', e.target.checked)}
                        />
                      </FormControl>

                      <FormControl display="flex" alignItems="center" flex={1}>
                        <Box flex={1}>
                          <FormLabel htmlFor="is_sympla" mb={0} fontSize="sm" fontWeight="medium">
                            Acesso Sympla
                          </FormLabel>
                          <Text fontSize="xs" color="gray.500">
                            Visualiza página Sympla
                          </Text>
                        </Box>
                        <Switch
                          id="is_sympla"
                          colorScheme="blue"
                          size="lg"
                          isChecked={isSympla}
                          onChange={e => formMethods.setValue('is_sympla', e.target.checked)}
                        />
                      </FormControl>
                    </Flex>
                  </VStack>
                </Box>

                <Divider />

                {/* Nível do Usuário - Apenas Admin */}
                {isAdmin && (
                  <>
                    <Box>
                      <Text fontSize="sm" fontWeight="semibold" color="gray.600" mb={4}>
                        NÍVEL DO USUÁRIO
                      </Text>
                      <HStack {...nivelGroup} spacing={3}>
                        {NIVEIS_CONFIG.map(nivelConfig => {
                          const radio = getRadioProps({ value: nivelConfig.value });
                          return (
                            <NivelRadioCard
                              key={nivelConfig.value}
                              nivelConfig={nivelConfig}
                              {...radio}
                            >
                              {nivelConfig.label}
                            </NivelRadioCard>
                          );
                        })}
                      </HStack>
                    </Box>

                    <Divider />
                  </>
                )}

                {/* Números WhatsApp */}
                {isAdmin && !isNewUser && (
                  <>
                    <Box>
                      <Flex align="center" gap={2} mb={4}>
                        <Icon as={WhatsappLogo} boxSize={5} color="green.500" weight="fill" />
                        <Text fontSize="sm" fontWeight="semibold" color="gray.600">
                          NÚMEROS WHATSAPP PERMITIDOS
                        </Text>
                      </Flex>
                      <Text fontSize="xs" color="gray.500" mb={3}>
                        Selecione quais números este usuário pode usar. Nenhum selecionado = acesso
                        a todos.
                      </Text>
                      {waNumbersLoading && (
                        <Flex justify="center" py={4}>
                          <Spinner size="sm" color="green.500" />
                        </Flex>
                      )}
                      {!waNumbersLoading && allWaNumbers.length === 0 && (
                        <Box
                          px={4}
                          py={3}
                          bg="gray.50"
                          borderRadius="lg"
                          borderWidth="1px"
                          borderColor="gray.200"
                        >
                          <Text fontSize="sm" color="gray.500">
                            Nenhum numero cadastrado. Cadastre em Configuracoes → Numeros WhatsApp.
                          </Text>
                        </Box>
                      )}
                      {!waNumbersLoading && allWaNumbers.length > 0 && (
                        <VStack spacing={2} align="stretch">
                          {allWaNumbers.map(num => {
                            const isSelected = selectedWaNumberIds.includes(num.id);
                            return (
                              <Box
                                key={num.id}
                                px={4}
                                py={3}
                                borderWidth="2px"
                                borderRadius="lg"
                                borderColor={isSelected ? 'green.400' : 'gray.200'}
                                bg={isSelected ? 'green.50' : 'white'}
                                cursor="pointer"
                                transition="all 0.2s"
                                _hover={{
                                  borderColor: isSelected ? 'green.400' : 'gray.300',
                                }}
                                onClick={() => handleToggleWaNumber(num.id)}
                              >
                                <Flex align="center" gap={3}>
                                  <Box
                                    w={5}
                                    h={5}
                                    borderRadius="md"
                                    borderWidth="2px"
                                    borderColor={isSelected ? 'green.400' : 'gray.300'}
                                    bg={isSelected ? 'green.400' : 'white'}
                                    display="flex"
                                    alignItems="center"
                                    justifyContent="center"
                                    flexShrink={0}
                                  >
                                    {isSelected && (
                                      <Text fontSize="xs" color="white" fontWeight="bold">
                                        ✓
                                      </Text>
                                    )}
                                  </Box>
                                  <Text fontSize="sm" fontWeight="medium" color="gray.700">
                                    {num.label}
                                  </Text>
                                </Flex>
                              </Box>
                            );
                          })}
                        </VStack>
                      )}
                      {selectedWaNumberIds.length > 0 && (
                        <Button
                          size="xs"
                          variant="ghost"
                          colorScheme="gray"
                          mt={2}
                          onClick={() => setSelectedWaNumberIds([])}
                        >
                          Limpar seleção (acesso a todos)
                        </Button>
                      )}
                    </Box>
                    <Divider />
                  </>
                )}

                {/* Senha */}
                <Box>
                  <Flex justify="space-between" align="center" mb={4}>
                    <Box>
                      <Text fontSize="sm" fontWeight="semibold" color="gray.600">
                        {isNewUser ? 'SENHA DE ACESSO' : 'ALTERAR SENHA'}
                      </Text>
                      {!isNewUser && (
                        <Text fontSize="xs" color="gray.500">
                          Deixe em branco para manter a senha atual
                        </Text>
                      )}
                    </Box>
                    <Button
                      size="sm"
                      variant="outline"
                      leftIcon={<Key size={16} />}
                      onClick={handleGeneratePassword}
                    >
                      Gerar senha
                    </Button>
                  </Flex>
                  <Flex gap={4} w="100%" direction={{ base: 'column', md: 'row' }}>
                    <Box flex={1}>
                      <InputForm
                        name="password"
                        label="Nova senha"
                        placeholder="Digite a senha"
                        type="password"
                      />
                    </Box>
                    <Box flex={1}>
                      <InputForm
                        name="confirmPassword"
                        label="Confirmar senha"
                        placeholder="Confirme a senha"
                        type="password"
                      />
                    </Box>
                  </Flex>
                </Box>
              </VStack>
            </Box>

            {/* Footer com botões */}
            <Box px={6} py={4} bg="gray.50" borderTop="1px" borderColor="gray.100">
              <Flex justify="space-between">
                <Button variant="ghost" onClick={() => navigate(-1)}>
                  Cancelar
                </Button>
                <Button
                  type="submit"
                  colorScheme="blue"
                  isLoading={isSubmitting}
                  loadingText="Salvando..."
                >
                  {isNewUser ? 'Criar Usuário' : 'Salvar Alterações'}
                </Button>
              </Flex>
            </Box>
          </Box>
        </FormProvider>
      </form>
    </Box>
  );
}
